/**
 * App.jsx
 *
 * Root component. Handles:
 *   - Auth state (sign-in screen vs main app)
 *   - Tab routing
 *   - Sheet data loading (passed down as props)
 *   - Panel overlay (ContactTimeline, ContactForm)
 *   - Push notifications (background polling)
 */

import { useState, useCallback, useMemo, useRef, useEffect, memo, startTransition } from 'react';
import { useAuth } from './auth/useAuth.js';
import { useSheetData, invalidateSheetCache } from './hooks/useSheetData.js';
import { createDataRefreshCoordinator } from './utils/dataRefresh.js';
import { usePushNotifications } from './hooks/usePushNotifications.js';
import Layout from './components/Layout.jsx';
import Today from './pages/Today.jsx';
import InProgress from './pages/InProgress.jsx';
import Contacts from './pages/Contacts.jsx';
import Customers from './pages/Customers.jsx';
import Analytics from './pages/Analytics.jsx';
import Outreach from './pages/Outreach.jsx';
import SyncBigDough from './components/SyncBigDough.jsx';

const MemoToday      = memo(Today);
const MemoInProgress = memo(InProgress);
const MemoContacts   = memo(Contacts);
const MemoCustomers  = memo(Customers);
const MemoAnalytics  = memo(Analytics);
const MemoSyncBigDough = memo(SyncBigDough);
import { fetchSyncUsers, checkConnected, requestBackendGmailSync, pollCalendarSyncStatus, startOAuthConnect } from './api/sync.js';
import ContactTimeline from './components/ContactTimeline.jsx';
import ContactForm from './components/ContactForm.jsx';
import UnknownContactsPrompt from './components/UnknownContactsPrompt.jsx';
import CalendarSyncBanner from './components/CalendarSyncBanner.jsx';
import { dismissUnmatched, removeUnmatched } from './api/unmatched.js';
import { updateConfig, fetchConfig, fetchAmplitudeStats } from './api/backend.js';
import { computeHealth } from './utils/health.js';
import { buildScoresMap, computeAmplitudeGlobalStats } from './utils/amplitude.js';

const APP_SETTINGS_KEY = 'sales_dashboard_app_settings_v1';

// Ensure a contact object has all the enriched fields ContactTimeline expects,
// regardless of which page passed it. Fills gaps using local data bundle.
function enrichContact(contact, data) {
  if (!contact) return contact;
  // Already fully enriched (came from Contacts BQ page)
  if (contact._firm_name !== undefined && contact._arr !== undefined) return contact;

  const firms   = data?.firms   ?? [];
  const trials  = data?.trials  ?? [];

  // Find firm by firm_id
  const firm = firms.find((f) => f.firm_id && f.firm_id === contact.firm_id) ?? null;

  const _firm_name = contact._firm_name ?? firm?.firm_name ?? contact.firm_name ?? '';
  const _firm_aum  = contact._firm_aum  ?? (firm?.aum != null ? Number(firm.aum) : null);
  const _fund_type = contact._fund_type ?? firm?.fund_type ?? contact.fund_type ?? '';

  // _arr: expected_arr from firm (strip $ and commas, parse as float → store as $M)
  const _arr = contact._arr != null
    ? contact._arr
    : (firm?.expected_arr
        ? parseFloat(String(firm.expected_arr).replace(/[$,]/g, '')) || 0
        : 0);

  // _lastTouchDate: already computed by ContactTimeline from events, but surface it for the fallback
  const _lastTouchDate = contact._lastTouchDate ?? contact._last_touch_ts ?? '';

  const _firm_owner  = contact._firm_owner  ?? firm?.owner    ?? '';
  const _firm_hq_city = contact._firm_hq_city ?? firm?.hq_city ?? '';

  // Pod shops: surface the parent fund (e.g. Santa Rosa → Millennium) so the
  // timeline header can show it next to the firm name.
  const _parent_firm_id = contact._parent_firm_id ?? firm?.parent_firm_id ?? '';
  const _parent_firm_name = contact._parent_firm_name
    ?? (_parent_firm_id ? (firms.find((f) => f.firm_id === _parent_firm_id)?.firm_name ?? '') : '');

  return {
    ...contact,
    _firm_name,
    _firm_aum,
    _fund_type,
    _arr,
    _lastTouchDate,
    _firm_owner,
    _firm_hq_city,
    _parent_firm_id,
    _parent_firm_name,
  };
}

function settingsToConfigPayload(s) {
  return {
    daily_target:              String(s.dailyTarget            ?? 4),
    urgency_red_score:         String(s.urgencyRedScore        ?? 60),
    urgency_amber_score:       String(s.urgencyAmberScore      ?? 30),
    next_step_overdue_days:    String(s.nextStepOverdueDays    ?? 3),
    stale_days_1:              String(s.staleDays1             ?? 21),
    stale_days_2:              String(s.staleDays2             ?? 60),
    trial_default_days:        String(s.trialDefaultDays       ?? 30),
    renewal_red_days:          String(s.renewalRedDays         ?? 30),
    renewal_amber_days:        String(s.renewalAmberDays       ?? 60),
    stage_weight_trial:        String(s.stageWeightTrial       ?? 0.6),
    stage_weight_demo:         String(s.stageWeightDemo        ?? 0.3),
    stage_weight_warm:         String(s.stageWeightWarm        ?? 0.1),
    stage_weight_cold:         String(s.stageWeightCold        ?? 0.05),
    stage_weight_untouched:    String(s.stageWeightUntouched   ?? 0.02),
    health_login_green_days:   String(s.healthLoginGreenDays   ?? 3),
    health_login_amber_days:   String(s.healthLoginAmberDays   ?? 7),
    health_feature_green_count:String(s.healthFeatureGreenCount ?? 3),
    health_feature_amber_count:String(s.healthFeatureAmberCount ?? 2),
    calendar_keywords:         String(s.calendarKeywords       ?? 'demo,implied'),
    internal_domains:          String(s.internalDomains        ?? 'implied.com,lumosity.ai'),
    auto_sync_users:           String(s.autoSyncUsers          ?? 'ying@implied.com'),
    auto_sync_enabled:         s.autoSyncEnabled === false ? 'false' : 'true',
    brief_tone:                String(s.briefTone              ?? 'concise'),
    brief_focus:               String(s.briefFocus             ?? 'pipeline momentum'),
    brief_send_time:           String(s.briefSendTime          ?? '08:30'),
    brief_include_stats:       s.briefIncludeStats ? 'true' : 'false',
    brief_enabled:             s.briefEnabled === false ? 'false' : 'true',
    brief_recipients:          String(s.briefRecipients        ?? ''),
    brief_section_must_act:    s.briefSectionMustAct === false ? 'false' : 'true',
    brief_section_needs_reply: s.briefSectionNeedsReply === false ? 'false' : 'true',
    brief_section_usage_drop:  s.briefSectionUsageDrop === false ? 'false' : 'true',
    brief_urgent_group_cap:    String(s.briefUrgentGroupCap    ?? 6),
    brief_watch_group_cap:     String(s.briefWatchGroupCap     ?? 8),
    brief_needs_reply_limit:   String(s.briefNeedsReplyLimit   ?? 25),
    brief_needs_reply_hours:   String(s.briefNeedsReplyHours   ?? 72),
    brief_usage_drop_max:      String(s.briefUsageDropMax      ?? 8),
    bd_high_intent_days:       String(s.bdHighIntentDays  ?? 14),
    bd_batch_min:              String(s.bdBatchMin        ?? 3),
    cadence_stage_offsets:     String(s.cadenceStageOffsets    ?? '0,2,5,12'),
    cadence_tracked_patterns:  String(s.cadenceTrackedPatterns ?? ''),
    cadence_stage_rules:       String(s.cadenceStageRules      ?? ''),
  };
}

function loadAppSettings() {
  try {
    const raw = localStorage.getItem(APP_SETTINGS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function LoginScreen({ onSignIn, error, onDevBypass }) {
  const [loading, setLoading] = useState(false);
  const handleClick = async () => {
    setLoading(true);
    try { await onSignIn(); }
    finally { setLoading(false); }
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-card__logo">Sales Dashboard</div>
        <div className="login-card__sub">Internal pipeline for hedge &amp; mutual fund contacts</div>
        <button className="login-card__btn" onClick={handleClick} disabled={loading}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          {loading ? 'Signing in…' : 'Sign in with Google'}
        </button>
        {error && <div className="login-card__error">{error}</div>}
        {import.meta.env.DEV && (
          <button
            onClick={onDevBypass}
            style={{
              marginTop: 14, width: '100%', padding: '8px', fontSize: 12,
              background: 'none', border: '1px dashed #cbd5e1', borderRadius: 6,
              color: '#94a3b8', cursor: 'pointer',
            }}
          >
            Skip login (dev only)
          </button>
        )}
      </div>
    </div>
  );
}

// Minimal mock data used in dev-bypass mode so pages render without real API calls
const DEV_DATA = {
  contacts: [], firms: [], demos: [], trials: [],
  outreachLog: [], config: {}, gmailUnmatched: [],
};
const DEV_GET_TOKEN = async () => 'dev-token-placeholder';

export default function App() {
  const { isSignedIn, userEmail, userName, userAvatar, getToken, signIn, signOut } = useAuth();
  const [authError, setAuthError] = useState(null);
  // Persisted so a refresh keeps you on the page you were on instead of
  // bouncing back to Today.
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const t = localStorage.getItem('salesdash_active_tab');
      if (['today', 'outreach', 'inprogress', 'contacts', 'customers', 'analytics', 'sync'].includes(t)) return t;
    } catch { /* storage unavailable — default below */ }
    return 'today';
  });
  useEffect(() => {
    try { localStorage.setItem('salesdash_active_tab', activeTab); } catch { /* ignore */ }
  }, [activeTab]);
  // Track which tabs have been visited so we only mount a page on first visit,
  // then keep it mounted (display:none) rather than unmounting (preserves local state).
  const [mountedTabs, setMountedTabs] = useState(() => new Set([activeTab]));
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    startTransition(() => {
      setMountedTabs((prev) => prev.has(tab) ? prev : new Set([...prev, tab]));
    });
  }, []);
  const [panel, setPanel] = useState(null);
  const [highlightContact, setHighlightContact] = useState(null);
  const [appSettings, setAppSettings] = useState(() => {
    const stored = loadAppSettings();
    return stored ?? {
      // Daily goals
      dailyTarget: 4,
      // Urgency thresholds
      urgencyRedScore: 60,
      urgencyAmberScore: 30,
      nextStepOverdueDays: 3,
      staleDays1: 21,
      staleDays2: 60,
      // Trial
      trialDefaultDays: 30,
      renewalRedDays: 30,
      renewalAmberDays: 60,
      // Pipeline probability weights
      stageWeightTrial: 0.6,
      stageWeightDemo: 0.3,
      stageWeightWarm: 0.1,
      stageWeightCold: 0.05,
      stageWeightUntouched: 0.02,
      // Health scoring
      healthLoginGreenDays: 3,
      healthLoginAmberDays: 7,
      healthFeatureGreenCount: 3,
      healthFeatureAmberCount: 2,
      // BD / Cold Outreach
      bdHighIntentDays: 14,
      bdBatchMin: 3,
      // Cadence (First Send / Follow-up schedule)
      cadenceStageOffsets: '0,2,5,12',
      cadenceTrackedPatterns: '',
      cadenceStageRules: '',
      // Sync config
      calendarKeywords: 'demo,implied',
      internalDomains: 'implied.com,lumosity.ai',
      autoSyncUsers: 'ying@implied.com',
      autoSyncEnabled: true,
      // Sales brief email
      briefTone: 'concise',
      briefFocus: 'pipeline momentum',
      briefSendTime: '08:30',
      briefIncludeStats: true,
      briefEnabled: true,
      briefRecipients: '',
      briefSectionMustAct: true,
      briefSectionNeedsReply: true,
      briefSectionUsageDrop: true,
      briefUrgentGroupCap: 6,
      briefWatchGroupCap: 8,
      briefNeedsReplyLimit: 25,
      briefNeedsReplyHours: 72,
      briefUsageDropMax: 8,
    };
  });
  const [settingsBootedFromBackend, setSettingsBootedFromBackend] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaveError, setSettingsSaveError] = useState('');
  const [syncingGmail, setSyncingGmail]       = useState(false);
  const [syncGmailError, setSyncGmailError]   = useState(null);
  const [syncingCalendar, setSyncingCalendar] = useState(false);
  const [syncCalendarError, setSyncCalendarError] = useState(null);
  const [syncUsers, setSyncUsers] = useState([]);
  const [selectedSyncUsers, setSelectedSyncUsers] = useState([]);
  const [isConnected, setIsConnected] = useState(null); // null=unknown, true/false
  const [unknownBusyEmail, setUnknownBusyEmail] = useState(null);
  const [dismissedEmails, setDismissedEmails] = useState(new Set());
  // Cross-channel: emails handled (matched/added) this session — instantly clear from all banners
  const [handledEmails, setHandledEmails] = useState(new Set());
  const [highlightDemoId,  setHighlightDemoId]  = useState(null);
  const [highlightTrialId, setHighlightTrialId] = useState(null);
  const [dismissedCalendarEmails, setDismissedCalendarEmails] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('dismissedCalendarAttendees') || '[]')); }
    catch { return new Set(); }
  });

  // Dev-only bypass — never active in production builds
  const [devMode, setDevMode] = useState(
    () => import.meta.env.DEV && localStorage.getItem('dev_bypass') === '1'
  );
  const handleDevBypass = useCallback(() => {
    localStorage.setItem('dev_bypass', '1');
    setDevMode(true);
  }, []);
  const handleDevSignOut = useCallback(() => {
    localStorage.removeItem('dev_bypass');
    setDevMode(false);
    signOut();
    invalidateSheetCache();
  }, [signOut]);

  const effectiveSignedIn  = isSignedIn || devMode;
  const effectiveGetToken  = useMemo(() => (devMode && !isSignedIn) ? DEV_GET_TOKEN : getToken, [devMode, isSignedIn, getToken]); // eslint-disable-line react-hooks/exhaustive-deps
  const effectiveEmail     = isSignedIn ? userEmail  : devMode ? 'dev@implied.com' : null;
  const effectiveName      = isSignedIn ? userName   : devMode ? 'Dev User'        : null;
  const effectiveAvatar    = isSignedIn ? userAvatar : null;

  // In dev-bypass mode we use DEV_DATA directly and skip the real fetch entirely
  const skipFetch = !isSignedIn;
  const { data: sheetData, loading, error: dataError, refresh, refreshOutreach, lastLoaded, patchTrial, upsertContact } = useSheetData({
    getToken: effectiveGetToken,
    skip: skipFetch,
  });
  const dataRefresh = useMemo(() => createDataRefreshCoordinator({ refresh, refreshOutreach }), [refresh, refreshOutreach]);

  const data        = (devMode && !isSignedIn) ? DEV_DATA : sheetData;
  const effectiveDataError = (devMode && !isSignedIn) ? null : dataError;

  // Amplitude scores — fetched once on sign-in, shared with Contacts + Analytics
  const [amplitudeScores,      setAmplitudeScores]      = useState(null);
  const [amplitudeGlobalStats, setAmplitudeGlobalStats] = useState(null);
  const [amplitudeUserMonthly, setAmplitudeUserMonthly] = useState([]);
  const [amplitudeUserWeekly,  setAmplitudeUserWeekly]  = useState([]);
  const [watchlistMap,         setWatchlistMap]         = useState({});
  useEffect(() => {
    if (!effectiveSignedIn || !effectiveGetToken) return;
    fetchAmplitudeStats(effectiveGetToken, 'all')
      .then((d) => {
        const um = d.userMonthly ?? [];
        setAmplitudeScores(buildScoresMap(um));
        setAmplitudeGlobalStats(computeAmplitudeGlobalStats(um));
        setAmplitudeUserMonthly(um);
        setAmplitudeUserWeekly(d.userWeekly ?? []);
        // Build email (lowercase) → { totalSaves, lastSave } map
        const wm = {};
        for (const r of (d.userWatchlist ?? [])) {
          if (r.user_id) wm[r.user_id.toLowerCase()] = { totalSaves: r.total_saves, lastSave: r.last_save };
        }
        setWatchlistMap(wm);
      })
      .catch(() => {}); // non-critical, fail silently
  }, [effectiveSignedIn, effectiveGetToken]);

  // Push notifications (Phase B.3) — runs in the background once signed in
  usePushNotifications({
    data,
    onNavigate: setActiveTab,
  });

  const loadSyncUsers = useCallback(async () => {
    if (!effectiveSignedIn) return;
    try {
      const users = await fetchSyncUsers(effectiveGetToken);
      const merged = Array.from(new Set([...(users ?? []), effectiveEmail].filter(Boolean).map((u) => String(u).toLowerCase()))).sort();
      setSyncUsers(merged);
      setSelectedSyncUsers((prev) => {
        // If nothing was previously selected (first load), default to ALL accounts.
        if (prev.length === 0) return merged;
        const prevSet = new Set(prev.map((u) => String(u).toLowerCase()));
        const next = merged.filter((u) => prevSet.has(u));
        return next.length > 0 ? next : merged;
      });
    } catch (e) {
      console.warn('[sync-users] load failed:', e.message);
      setSyncUsers([String(effectiveEmail ?? '').toLowerCase()].filter(Boolean));
      setSelectedSyncUsers([String(effectiveEmail ?? '').toLowerCase()].filter(Boolean));
    }
  }, [effectiveGetToken, effectiveSignedIn, effectiveEmail]);

  useEffect(() => {
    loadSyncUsers();
  }, [loadSyncUsers]);

  // Check if current user has granted Gmail/Calendar OAuth, and handle ?oauth= redirects
  useEffect(() => {
    if (!effectiveSignedIn) return;

    // Handle redirect back from /api/oauth/connect
    const params = new URLSearchParams(window.location.search);
    const oauthResult = params.get('oauth');
    if (oauthResult) {
      // Strip the ?oauth= param from the URL without a page reload
      params.delete('oauth');
      const newSearch = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));
      if (oauthResult === 'success') {
        // Token stored — reload sync users and mark connected
        setIsConnected(true);
        loadSyncUsers();
        return;
      }
    }

    checkConnected(effectiveGetToken)
      .then((connected) => setIsConnected(connected))
      .catch(() => setIsConnected(null));
  }, [effectiveSignedIn, effectiveGetToken, loadSyncUsers]);

  const handleConnect = useCallback(async () => {
    try {
      const redirect = window.location.origin + window.location.pathname;
      window.location.href = await startOAuthConnect(effectiveGetToken, redirect);
    } catch (e) {
      console.warn('[connect] Could not get token:', e.message);
    }
  }, [effectiveGetToken]);

  // On first successful data load, sync settings from backend config if no localStorage yet
  useEffect(() => {
    if (settingsBootedFromBackend || !data?.config) return;
    setSettingsBootedFromBackend(true);
    const cfg = data.config;
    const stored = loadAppSettings();
    setAppSettings((prev) => {
      const cfgNum = (key, fallback) => stored?.[key] != null ? prev[key] : (parseFloat(cfg[key]) || fallback);
      const cfgStr = (key, cfgKey)   => stored?.[key] != null ? prev[key] : (cfg[cfgKey ?? key] || prev[key]);
      return ({
      dailyTarget:            stored?.dailyTarget            != null ? prev.dailyTarget            : (parseInt(cfg['daily_target']) || prev.dailyTarget),
      urgencyRedScore:        cfgNum('urgencyRedScore',        60),
      urgencyAmberScore:      cfgNum('urgencyAmberScore',      30),
      nextStepOverdueDays:    cfgNum('nextStepOverdueDays',    3),
      staleDays1:             cfgNum('staleDays1',             21),
      staleDays2:             cfgNum('staleDays2',             60),
      trialDefaultDays:       cfgNum('trialDefaultDays',       30),
      renewalRedDays:         cfgNum('renewalRedDays',         30),
      renewalAmberDays:       cfgNum('renewalAmberDays',       60),
      stageWeightTrial:       cfgNum('stageWeightTrial',       0.6),
      stageWeightDemo:        cfgNum('stageWeightDemo',        0.3),
      stageWeightWarm:        cfgNum('stageWeightWarm',        0.1),
      stageWeightCold:        cfgNum('stageWeightCold',        0.05),
      stageWeightUntouched:   cfgNum('stageWeightUntouched',   0.02),
      healthLoginGreenDays:   cfgNum('healthLoginGreenDays',   3),
      healthLoginAmberDays:   cfgNum('healthLoginAmberDays',   7),
      healthFeatureGreenCount:cfgNum('healthFeatureGreenCount',3),
      healthFeatureAmberCount:cfgNum('healthFeatureAmberCount',2),
      cadenceStageOffsets:    cfgStr('cadenceStageOffsets',    'cadence_stage_offsets'),
      cadenceTrackedPatterns: cfgStr('cadenceTrackedPatterns', 'cadence_tracked_patterns'),
      cadenceStageRules:      cfgStr('cadenceStageRules',      'cadence_stage_rules'),
      calendarKeywords:       cfgStr('calendarKeywords',       'calendar_keywords'),
      internalDomains:        cfgStr('internalDomains',        'internal_domains'),
      autoSyncUsers:          cfgStr('autoSyncUsers',          'auto_sync_users'),
      autoSyncEnabled:        stored?.autoSyncEnabled != null ? prev.autoSyncEnabled : (cfg['auto_sync_enabled'] !== 'false'),
      briefTone:              cfgStr('briefTone',              'brief_tone'),
      briefFocus:             cfgStr('briefFocus',             'brief_focus'),
      briefSendTime:          cfgStr('briefSendTime',          'brief_send_time'),
      briefIncludeStats:      stored?.briefIncludeStats != null ? prev.briefIncludeStats : (cfg['brief_include_stats'] !== 'false'),
      briefEnabled:           stored?.briefEnabled != null ? prev.briefEnabled : (cfg['brief_enabled'] !== 'false'),
      briefRecipients:        cfgStr('briefRecipients',        'brief_recipients'),
      briefSectionMustAct:    stored?.briefSectionMustAct != null ? prev.briefSectionMustAct : (cfg['brief_section_must_act'] !== 'false'),
      briefSectionNeedsReply: stored?.briefSectionNeedsReply != null ? prev.briefSectionNeedsReply : (cfg['brief_section_needs_reply'] !== 'false'),
      briefSectionUsageDrop:  stored?.briefSectionUsageDrop != null ? prev.briefSectionUsageDrop : (cfg['brief_section_usage_drop'] !== 'false'),
      briefUrgentGroupCap:    stored?.briefUrgentGroupCap    != null ? prev.briefUrgentGroupCap    : (parseFloat(cfg['brief_urgent_group_cap'])    || 6),
      briefWatchGroupCap:     stored?.briefWatchGroupCap     != null ? prev.briefWatchGroupCap     : (parseFloat(cfg['brief_watch_group_cap'])     || 8),
      briefNeedsReplyLimit:   stored?.briefNeedsReplyLimit   != null ? prev.briefNeedsReplyLimit   : (parseFloat(cfg['brief_needs_reply_limit'])   || 25),
      briefNeedsReplyHours:   stored?.briefNeedsReplyHours   != null ? prev.briefNeedsReplyHours   : (parseFloat(cfg['brief_needs_reply_hours'])   || 72),
      briefUsageDropMax:      stored?.briefUsageDropMax      != null ? prev.briefUsageDropMax      : (parseFloat(cfg['brief_usage_drop_max'])      || 8),
      bdHighIntentDays:       cfgNum('bdHighIntentDays',  14),
      bdBatchMin:             cfgNum('bdBatchMin',         3),
    });});
  }, [data?.config, settingsBootedFromBackend]);

  const handleSignIn = useCallback(async () => {
    setAuthError(null);
    try { await signIn(); }
    catch (e) { setAuthError(e.message); }
  }, [signIn]);

  const handleSignOut = useCallback(() => {
    signOut();
    invalidateSheetCache();
  }, [signOut]);

  const openTimeline = useCallback((contact) => {
    // If this is a thin contact (e.g. from email-map with only contact_id/name/email/firm_id),
    // merge in the fuller version from data.contacts before enriching.
    const contacts = data?.contacts ?? [];
    const fullContact = contacts.find((c) => c.contact_id && c.contact_id === contact?.contact_id) ?? contact;
    const enriched = enrichContact(fullContact, data);
    setPanel({ type: 'timeline', contact: enriched });
  }, [data]);

  const viewInContacts = useCallback((contact) => {
    setPanel(null);
    setHighlightContact(contact);
    setActiveTab('contacts');
    setMountedTabs((prev) => prev.has('contacts') ? prev : new Set([...prev, 'contacts']));
  }, []);

  const openAddContact = useCallback((prefill = {}) => {
    setPanel({ type: 'form', contact: null, prefill });
  }, []);

  const openEditContact = useCallback((contact) => {
    setPanel({ type: 'form', contact });
  }, []);

  const openSettings = useCallback(() => {
    setPanel({ type: 'settings' });
  }, []);

  // Opens the contact's timeline (which has the draft-email flow)
  const openDraftForContact = useCallback((contact) => {
    if (contact) openTimeline(contact);
  }, [openTimeline]);

  const closePanel = useCallback(() => setPanel(null), []);

  // ── Unknown-email prompt (from Gmail sync) ──────────────────────────────────
  const handleAddUnknown = useCallback((item) => {
    openAddContact({
      name:      item.name || '',
      email:     item.email || '',
      firm_name: item.firm_guess || '',
      source:    'Gmail Sync',
      _unmatchedEmail: item.email, // tells onSaved to clear the Gmail_Unmatched row
    });
  }, [openAddContact]);

  const handleIgnoreUnknown = useCallback((item) => {
    setDismissedEmails((prev) => new Set([...prev, item.email]));
    dismissUnmatched(effectiveGetToken, item.email)
      .then(() => { dataRefresh.refreshAll(); })
      .catch((e) => {
        console.warn('[unmatched] dismiss failed:', e.message);
        setDismissedEmails((prev) => { const n = new Set(prev); n.delete(item.email); return n; });
      });
  }, [effectiveGetToken, dataRefresh]);

  // Last saved contact (create or edit) — Contacts applies it to its local
  // table rows instantly instead of waiting for its server refetch.
  const [savedContactPing, setSavedContactPing] = useState(null);

  const handleFormSaved = useCallback(async (prefill, saved) => {
    closePanel();
    // Optimistic: reflect the save everywhere the moment the panel closes —
    // the background refresh below confirms with server truth a few seconds
    // later. For NEW contacts, also link any trial row the server auto-links
    // by email; for edits, ping the Contacts table so it patches its rows.
    if (saved?.contact_id) {
      upsertContact(saved);
      setSavedContactPing({ contact: saved, ts: Date.now() });
      if (saved._isNew) {
        const email = String(saved.email ?? '').toLowerCase();
        const t = email ? (sheetData?.trials ?? []).find(
          (r) => !String(r.contact_id ?? '').trim() && String(r.amplitude_user_id ?? '').toLowerCase() === email,
        ) : null;
        if (t) {
          patchTrial(t.trial_id, {
            contact_id: saved.contact_id, contact_ids: saved.contact_id,
            firm_id: saved.firm_id || t.firm_id,
            suggested_contact_id: '', suggested_contact_name: '',
          });
        }
      }
    }
    const emailToClean = prefill?._unmatchedEmail || prefill?.email;
    if (emailToClean) {
      try { await removeUnmatched(effectiveGetToken, emailToClean); }
      catch (e) { console.warn('[unmatched] remove failed:', e.message); }
    }
    // Mark the email as handled so all banners clear it instantly
    if (prefill?.email) {
      setHandledEmails((prev) => new Set([...prev, prefill.email.toLowerCase()]));
    }
    await dataRefresh.refreshAllTwice();
  }, [closePanel, effectiveGetToken, dataRefresh, sheetData, patchTrial, upsertContact]);

  const [analyticsRefreshKey, setAnalyticsRefreshKey] = useState(0);
  const handleSyncDone = useCallback(() => {
    dataRefresh.refreshAll();
    setAnalyticsRefreshKey((k) => k + 1);
  }, [dataRefresh]);

  const handleRefreshAll = useCallback(() => {
    dataRefresh.refreshAll();
  }, [dataRefresh]);

  const handleSyncGmail = useCallback(async () => {
    if (syncingGmail) return;
    setSyncingGmail(true);
    setSyncGmailError(null);
    try {
      await requestBackendGmailSync(effectiveGetToken, selectedSyncUsers);
      dataRefresh.refreshOutreachOnly();
      setSyncGmailError(null);
    } catch (e) {
      console.warn('[sync] Gmail sync failed:', e.message);
      setSyncGmailError('Sync failed: ' + e.message);
    } finally {
      setSyncingGmail(false);
    }
  }, [syncingGmail, effectiveGetToken, selectedSyncUsers, dataRefresh]);

  const handleSyncCalendar = useCallback(async () => {
    if (syncingCalendar) return;
    setSyncingCalendar(true);
    setSyncCalendarError(null);
    try {
      const st = await pollCalendarSyncStatus(effectiveGetToken, selectedSyncUsers);
      await dataRefresh.refreshAll();

      // Surface per-user reconnect errors stored by the background worker.
      const reconnectEmails = st?.reconnectEmails ?? [];
      if (reconnectEmails.length > 0) {
        setSyncCalendarError(`${reconnectEmails.join(', ')} need${reconnectEmails.length === 1 ? 's' : ''} to reconnect — click to fix`);
      } else {
        setSyncCalendarError(null);
      }
    } catch (e) {
      console.warn('[sync] Calendar sync failed:', e.message);
      setSyncCalendarError('Sync failed: ' + e.message);
    } finally {
      setSyncingCalendar(false);
    }
  }, [syncingCalendar, effectiveGetToken, selectedSyncUsers, dataRefresh]);

  const handleReconnectCalendar = useCallback(async () => {
    try {
      const redirect = window.location.origin + window.location.pathname;
      window.location.href = await startOAuthConnect(effectiveGetToken, redirect);
    } catch (e) {
      console.warn('[reconnect] Could not get token:', e.message);
    }
  }, [effectiveGetToken]);

  // Calendar Sync unknown attendees — computed by a DB anti-join on the backend,
  // then filtered client-side only for locally dismissed emails.
  const calendarUnknownItems = useMemo(() => {
    const raw = data?.calendarUnknownAttendees ?? [];
    return raw.filter((item) => {
      const e = String(item.email ?? '').toLowerCase().trim();
      return !dismissedCalendarEmails.has(e) && !handledEmails.has(e);
    });
  }, [data, dismissedCalendarEmails, handledEmails]);

  const navigateToDemo = useCallback((demoId) => {
    setActiveTab('inprogress');
    startTransition(() => {
      setMountedTabs((prev) => prev.has('inprogress') ? prev : new Set([...prev, 'inprogress']));
    });
    setHighlightDemoId(demoId);
  }, []);

  const navigateToTrial = useCallback((trialId) => {
    setActiveTab('inprogress');
    startTransition(() => {
      setMountedTabs((prev) => prev.has('inprogress') ? prev : new Set([...prev, 'inprogress']));
    });
    setHighlightTrialId(trialId);
  }, []);

  const dismissCalendarEmail = useCallback((email) => {
    setDismissedCalendarEmails((prev) => {
      const next = new Set(prev);
      next.add(email);
      try { localStorage.setItem('dismissedCalendarAttendees', JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);

  // Shared handler: called by any banner after match or add — must be above early returns
  const handleContactHandled = useCallback((email) => {
    if (!email) return;
    setHandledEmails((prev) => new Set([...prev, email.toLowerCase()]));
    dataRefresh.refreshAll();
  }, [dataRefresh]);

  // Must be above the early return — hooks cannot be called after a conditional return
  const trialHeaderStats = useMemo(() => {
    const active = (data?.trials ?? []).filter((t) => String(t.status ?? '').toLowerCase() === 'active');
    return {
      count:    active.length,
      critical: active.filter((t) => computeHealth(t, appSettings).score === 'red').length,
    };
  }, [data?.trials, appSettings]);

  if (!effectiveSignedIn) {
    return <LoginScreen onSignIn={handleSignIn} error={authError} onDevBypass={handleDevBypass} />;
  }

  const calendarEmailSet = new Set(calendarUnknownItems.map((i) => String(i.email ?? '').toLowerCase().trim()));

  const pendingUnknown = (data?.gmailUnmatched ?? [])
    .filter((r) => String(r.status ?? '').toLowerCase() === 'pending' && r.email
      && !dismissedEmails.has(r.email) && !handledEmails.has(r.email.toLowerCase())
      && !calendarEmailSet.has(r.email.toLowerCase().trim()));

  return (
    <>
      <Layout
        activeTab={activeTab}
        topbarSuffix={activeTab === 'inprogress'
          ? `${trialHeaderStats.count} active trials${trialHeaderStats.critical > 0 ? ` · ${trialHeaderStats.critical} critical` : ''}`
          : null}
        onTabChange={handleTabChange}
        onRefresh={refresh}
        lastLoaded={lastLoaded}
        userEmail={effectiveEmail}
        userName={effectiveName}
        userAvatar={effectiveAvatar}
        onSignOut={devMode && !isSignedIn ? handleDevSignOut : handleSignOut}
        onSyncGmail={handleSyncGmail}
        syncingGmail={syncingGmail}
        syncGmailError={syncGmailError}
        syncUsers={syncUsers}
        selectedSyncUsers={selectedSyncUsers}
        onSyncUsersChange={setSelectedSyncUsers}
        onSyncCalendar={handleSyncCalendar}
        syncingCalendar={syncingCalendar}
        syncCalendarError={syncCalendarError}
        onReconnectCalendar={handleReconnectCalendar}
        isConnected={isConnected}
        onConnect={handleConnect}
        onOpenSettings={openSettings}
      >
        <div style={{ display: activeTab === 'today' ? 'block' : 'none' }}>
          {calendarUnknownItems.length > 0 && (
            <CalendarSyncBanner
              items={calendarUnknownItems}
              getToken={effectiveGetToken}
              firms={data?.firms ?? []}
              onNavigateToDemo={navigateToDemo}
              onDismiss={dismissCalendarEmail}
              onAdd={(prefill) => openAddContact({ ...prefill, source: 'Calendar Sync' })}
              onHandled={handleContactHandled}
            />
          )}
          {pendingUnknown.length > 0 && (
            <UnknownContactsPrompt
              items={pendingUnknown}
              getToken={effectiveGetToken}
              firms={data?.firms ?? []}
              onAdd={(prefill) => openAddContact({ ...prefill, source: 'Gmail Sync', _unmatchedEmail: prefill.email })}
              onIgnore={handleIgnoreUnknown}
              onHandled={handleContactHandled}
            />
          )}
          <MemoToday
            data={data}
            getToken={effectiveGetToken}
            onOpenTimeline={openTimeline}
            onTabChange={handleTabChange}
            onNavigateToDemo={navigateToDemo}
            onNavigateToTrial={navigateToTrial}
            onRefreshAll={dataRefresh.refreshAll}
            onRefreshOutreach={dataRefresh.refreshOutreachOnly}
            userEmail={effectiveEmail}
            userName={effectiveName}
            settings={appSettings}
          />
        </div>
        {mountedTabs.has('inprogress') && (
          <div className="tab-wrap--inprogress" style={{ display: activeTab === 'inprogress' ? 'block' : 'none' }}>
            <MemoInProgress
              data={data}
              onOpenTimeline={openTimeline}
              onOpenAddContact={openAddContact}
              onOpenDraft={openDraftForContact}
              getToken={effectiveGetToken}
              patchTrial={patchTrial}
              upsertContact={upsertContact}
              pendingUnknownCount={pendingUnknown.length}
              onRefresh={handleRefreshAll}
              highlightDemoId={highlightDemoId}
              onClearHighlight={() => setHighlightDemoId(null)}
              highlightTrialId={highlightTrialId}
              onClearTrialHighlight={() => setHighlightTrialId(null)}
              amplitudeScores={amplitudeScores}
              watchlistMap={watchlistMap}
              settings={appSettings}
              userEmail={effectiveEmail}
              userName={effectiveName}
            />
          </div>
        )}
        {mountedTabs.has('contacts') && (
          <div className="tab-panel--contacts" style={{ display: activeTab === 'contacts' ? 'block' : 'none' }}>
            <MemoContacts
              data={data}
              savedContactPing={savedContactPing}
              onOpenTimeline={openTimeline}
              onOpenAddContact={openAddContact}
              onEditContact={openEditContact}
              getToken={effectiveGetToken}
              onRefresh={refresh}
              highlightContact={highlightContact}
              onClearHighlight={() => setHighlightContact(null)}
              amplitudeScores={amplitudeScores}
              settings={appSettings}
            />
          </div>
        )}
        {mountedTabs.has('customers') && (
          <div className="tab-wrap--customers" style={{ display: activeTab === 'customers' ? 'block' : 'none' }}>
            <MemoCustomers
              data={data}
              onOpenTimeline={openTimeline}
              getToken={getToken}
              onRefresh={refresh}
              amplitudeScores={amplitudeScores}
              settings={appSettings}
            />
          </div>
        )}
        {mountedTabs.has('analytics') && (
          <div style={{ display: activeTab === 'analytics' ? 'block' : 'none' }}>
            <MemoAnalytics data={data} getToken={effectiveGetToken} onViewInContacts={openTimeline} onAddContact={openAddContact} excludeEmails={calendarEmailSet} handledEmails={handledEmails} onHandled={handleContactHandled} refreshKey={analyticsRefreshKey} />
          </div>
        )}
        {mountedTabs.has('outreach') && (
          <div style={{ display: activeTab === 'outreach' ? 'block' : 'none' }}>
            <Outreach getToken={effectiveGetToken} data={data} onOpenTimeline={openTimeline} amplitudeScores={amplitudeScores} />
          </div>
        )}
        {mountedTabs.has('sync') && (
          <div style={{ display: activeTab === 'sync' ? 'block' : 'none' }}>
            <MemoSyncBigDough getToken={effectiveGetToken} onSyncDone={handleSyncDone} onDeleteDone={handleSyncDone} outreachLog={data?.outreachLog ?? []} firms={data?.firms ?? []} onHandled={handleContactHandled} excludeEmails={calendarEmailSet} onAdd={(prefill) => openAddContact({ ...prefill, source: 'BigDough' })} />
          </div>
        )}
        <div style={{ display: activeTab === 'settings' ? 'block' : 'none' }}>
          <SettingsPanel
            settings={appSettings}
            saving={settingsSaving}
            saveError={settingsSaveError}
            getToken={effectiveGetToken}
            onChange={(next) => {
              setAppSettings(next);
              try { localStorage.setItem(APP_SETTINGS_KEY, JSON.stringify(next)); } catch {}
              if (next?.dailyTarget != null) {
                dataRefresh.refreshAll();
              }
            }}
            onSave={async (next) => {
              setSettingsSaving(true);
              setSettingsSaveError('');
              try {
                await updateConfig(effectiveGetToken, settingsToConfigPayload(next));
                dataRefresh.refreshAll();
              } catch (e) {
                setSettingsSaveError(`Could not save to backend: ${e.message}`);
              } finally {
                setSettingsSaving(false);
              }
            }}
          />
        </div>
      </Layout>

      {/* Panel overlay */}
      {panel && (
        <div
          className="panel-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) closePanel(); }}
        >
          <div className="panel">
            <div className="panel__header">
              <div className="panel__title">
                {panel.type === 'timeline' ? 'Contact Timeline'
                  : panel.contact ? 'Edit Contact'
                  : panel.type === 'settings' ? 'Settings'
                  : 'Add Contact'}
              </div>
              <button className="panel__close" onClick={closePanel}>×</button>
            </div>
            <div className="panel__body">
              {panel.type === 'timeline' && (
                <ContactTimeline
                  contact={panel.contact}
                  data={data}
                  getToken={effectiveGetToken}
                  onViewInContacts={viewInContacts}
                  amplitudeScores={amplitudeScores}
                  amplitudeGlobalStats={amplitudeGlobalStats}
                  amplitudeUserMonthly={amplitudeUserMonthly}
                  amplitudeUserWeekly={amplitudeUserWeekly}
                  onContactUpdate={(updated) => {
                    setPanel((p) => p ? { ...p, contact: updated } : p);
                    refresh();
                  }}
                />
              )}
              {panel.type === 'form' && (
                <ContactForm
                  contact={panel.contact}
                  prefill={panel.prefill}
                  data={data}
                  getToken={effectiveGetToken}
                  onSaved={(created) => handleFormSaved(panel.prefill, created)}
                  onCancel={closePanel}
                />
              )}
              {panel.type === 'settings' && (
                <SettingsPanel
                  settings={appSettings}
                  saving={settingsSaving}
                  saveError={settingsSaveError}
                  getToken={effectiveGetToken}
                  onChange={(next) => {
                    setAppSettings(next);
                    try { localStorage.setItem(APP_SETTINGS_KEY, JSON.stringify(next)); } catch {}
                  }}
                  onSave={async (next) => {
                    setSettingsSaving(true);
                    setSettingsSaveError('');
                    try {
                      await updateConfig(effectiveGetToken, {
                        daily_target: String(next.dailyTarget ?? 4),
                        brief_tone: String(next.briefTone ?? 'concise'),
                        brief_focus: String(next.briefFocus ?? 'pipeline momentum'),
                        brief_send_time: String(next.briefSendTime ?? '08:30'),
                        brief_include_stats: next.briefIncludeStats ? 'true' : 'false',
                      });
                      refresh();
                    } catch (e) {
                      setSettingsSaveError(`Could not save to backend: ${e.message}`);
                    } finally {
                      setSettingsSaving(false);
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SettingsSection({ title, children }) {
  return (
    <section style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 14 }}>
        {title}
      </div>
      {children}
    </section>
  );
}

function NumField({ label, hint, value, min, max, step, onChange }) {
  const focused = useRef(false);
  const [raw, setRaw] = useState(String(value ?? ''));
  // Only sync from parent while not focused (don't interrupt typing)
  const prev = useRef(value);
  if (prev.current !== value && !focused.current) {
    prev.current = value;
    setRaw(String(value ?? ''));
  }
  prev.current = value;
  const inp = { display: 'block', width: '100%', marginTop: 5, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', fontSize: 13 };
  return (
    <label style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)', marginBottom: 10 }}>
      {label}
      <input
        type="number" min={min} max={max} step={step ?? 1}
        value={raw}
        onFocus={() => { focused.current = true; }}
        onChange={(e) => {
          setRaw(e.target.value);
          const n = parseFloat(e.target.value);
          // Live-update parent so Save captures current value even without blur
          if (!isNaN(n)) onChange({ target: { value: e.target.value } });
        }}
        onBlur={() => {
          focused.current = false;
          const n = parseFloat(raw);
          if (isNaN(n)) {
            setRaw(String(value ?? '')); // reset invalid input
          } else {
            onChange({ target: { value: String(n) } }); // commit final value
          }
        }}
        style={inp}
      />
      {hint && <span style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginTop: 3 }}>{hint}</span>}
    </label>
  );
}

// Chip-style add/remove editor for a comma-separated domain list (e.g. internal
// email domains to auto-ignore). `value`/`onChange` work with the same
// comma-joined string shape the setting is stored/sent to the backend as.
function DomainListEditor({ value, onChange, placeholder }) {
  const domains = String(value ?? '').split(',').map((d) => d.trim()).filter(Boolean);
  const [draft, setDraft] = useState('');

  function commit(list) { onChange({ target: { value: list.join(',') } }); }

  function addDraft() {
    const d = draft.trim().toLowerCase();
    setDraft('');
    if (!d || domains.includes(d)) return;
    commit([...domains, d]);
  }

  function removeDomain(d) { commit(domains.filter((x) => x !== d)); }

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {domains.map((d) => (
          <span key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 99, padding: '3px 6px 3px 10px' }}>
            {d}
            <button
              type="button"
              onClick={() => removeDomain(d)}
              aria-label={`Remove ${d}`}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 13, lineHeight: 1, padding: '0 2px' }}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addDraft(); } }}
          placeholder={placeholder}
          style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', fontSize: 13 }}
        />
        <button type="button" className="btn btn--sm" onClick={addDraft} disabled={!draft.trim()}>Add</button>
      </div>
    </div>
  );
}

const GTM_FIELDS = [
  { key: 'gtm_wait_days',          label: 'Wait time between touches',     hint: 'Minimum days before re-contacting the same person.',          unit: 'days', default: '7'    },
  { key: 'gtm_weekly_target',      label: 'Weekly outreach target',        hint: 'Target outbound emails per week (shown on Today + Outreach).', unit: 'emails', default: '2500' },
  { key: 'gtm_reply_window_days',  label: 'Reply window',                  hint: 'Days to wait for a reply before considering re-outreach.',     unit: 'days', default: '14'   },
  { key: 'gtm_response_rate_warn', label: 'Reply rate warning threshold',  hint: 'Flag a subject line in Analytics when reply rate falls below this.', unit: '%', default: '5' },
];

function GtmSettingsSection({ getToken }) {
  const [vals, setVals]     = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [err, setErr]       = useState('');

  useEffect(() => {
    if (!getToken) return;
    fetchConfig(getToken)
      .then((cfg) => {
        const v = {};
        for (const f of GTM_FIELDS) v[f.key] = cfg[f.key] ?? f.default;
        setVals(v);
      })
      .catch(() => {});
  }, [getToken]);

  async function save() {
    setSaving(true); setErr(''); setSaved(false);
    try {
      await updateConfig(getToken, vals);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  const inp = { display: 'block', width: '100%', marginTop: 4, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', fontSize: 13 };

  return (
    <SettingsSection title="GTM / Outreach">
      <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 12 }}>
        Controls the Outreach page queue and weekly progress. Values marked as placeholders should be confirmed with your manager.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        {GTM_FIELDS.map((f) => (
          <label key={f.key} style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)' }}>
            {f.label}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="number" min={0}
                value={vals[f.key] ?? f.default}
                onChange={(e) => setVals((p) => ({ ...p, [f.key]: e.target.value }))}
                style={{ ...inp, width: 90, display: 'inline-block', textAlign: 'right' }}
              />
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{f.unit}</span>
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginTop: 3 }}>{f.hint}</span>
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button className="btn btn--primary btn--sm" onClick={save} disabled={saving || !getToken}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save GTM settings'}
        </button>
        {err && <span style={{ fontSize: 12, color: '#ef4444' }}>{err}</span>}
      </div>
    </SettingsSection>
  );
}

// Seed patterns shown when no custom cadence rules have been saved yet —
// mirrors the backend's hardcoded default (G-number series + Spec*) so the
// editor never opens blank.
const DEFAULT_CADENCE_SEED_PATTERNS = ['G*', 'Spec*'];

// Reads the saved rules JSON for display; when empty/unset, seeds the editor
// from the legacy single-sequence settings (cadenceStageOffsets/
// cadenceTrackedPatterns) so upgrading users see their current *effective*
// config as an editable list rather than a blank one. This seed is
// display-only — nothing is written back to cadenceStageRules until the
// user actually edits a row, matching the backend's fallback: untouched,
// the legacy fields keep driving behavior exactly as before.
function parseRulesJsonForEditing(rulesJson, legacyOffsets, legacyPatterns) {
  if (rulesJson && rulesJson.trim()) {
    try {
      const parsed = JSON.parse(rulesJson);
      if (Array.isArray(parsed) && parsed.length) {
        return parsed.map((r) => ({ pattern: String(r?.pattern ?? ''), offsets: String(r?.offsets ?? '') }));
      }
    } catch { /* fall through to legacy seed */ }
  }
  const patterns = String(legacyPatterns ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const seedPatterns = patterns.length ? patterns : DEFAULT_CADENCE_SEED_PATTERNS;
  return seedPatterns.map((pattern) => ({ pattern, offsets: String(legacyOffsets ?? '0,2,5,12') }));
}

function CadenceRulesEditor({ rulesJson, legacyOffsets, legacyPatterns, onChange }) {
  const rows = parseRulesJsonForEditing(rulesJson, legacyOffsets, legacyPatterns);
  const isCustom = !!(rulesJson && rulesJson.trim());

  // Deliberately does NOT strip blank-pattern rows — a freshly-added row is
  // blank until the user types a pattern into it, and filtering here would
  // delete it before that keystroke ever lands. The backend already skips
  // any rule with an empty pattern when it parses this JSON, so a blank row
  // sitting in saved settings is harmless, just inert.
  const commit = (nextRows) => onChange(JSON.stringify(nextRows));
  const updateRow = (i, patch) => commit(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => commit(rows.filter((_, idx) => idx !== i));
  const addRow = () => commit([...rows, { pattern: '', offsets: '0,2,5,12' }]);

  const smallInp = { display: 'block', width: '100%', padding: '6px 9px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)', fontSize: 12.5 };

  return (
    <div>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 8 }}>
        Follow-up sequence per list name (first matching pattern wins)
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8 }}>
          <input
            type="text"
            value={r.pattern}
            placeholder="G*, Spec*, ..."
            style={smallInp}
            onChange={(e) => updateRow(i, { pattern: e.target.value })}
          />
          <input
            type="text"
            value={r.offsets}
            placeholder="0,2,5,12"
            style={smallInp}
            onChange={(e) => updateRow(i, { offsets: e.target.value })}
          />
          <button
            onClick={() => removeRow(i)}
            title="Remove rule"
            style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 7, padding: '0 10px', cursor: 'pointer', color: 'var(--text-2)', fontSize: 13 }}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        onClick={addRow}
        style={{ background: 'transparent', border: '1px dashed var(--border)', borderRadius: 7, padding: '6px 12px', cursor: 'pointer', color: 'var(--text-2)', fontSize: 12.5 }}
      >
        + Add rule
      </button>
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 10, lineHeight: 1.5 }}>
        Each rule is a list-name pattern (wildcard <code>*</code> supported) paired with its own
        comma-separated Follow-up day-offsets from First Send — first value 0 is First Send itself, each
        later value is days-after-First-Send for that Follow-up stage (a Saturday due date always shifts
        to Sunday). The first rule whose pattern matches a list name wins, top to bottom — put more
        specific patterns above broader ones. A list name matching no rule at all gets no Follow-up
        tracking at all.
        {!isCustom && ' Showing the current default sequence — edit or add a rule to switch to custom per-pattern sequences.'}
      </div>
    </div>
  );
}

function SettingsPanel({ settings, onChange, onSave, saving, saveError, getToken }) {
  const update = (patch) => onChange?.({ ...settings, ...patch });
  const inp = { display: 'block', width: '100%', marginTop: 5, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', fontSize: 13 };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Daily goal ───────────────────────────────────────────────────── */}
      <SettingsSection title="Daily Goal">
        <NumField
          label="Missions per day"
          hint="Controls the goal shown in Today's win-condition card."
          value={settings.dailyTarget ?? 4}
          min={1} max={500}
          onChange={(e) => update({ dailyTarget: Math.max(1, Math.min(500, Number(e.target.value) || 1)) })}
        />
      </SettingsSection>

      {/* ── Urgency thresholds ───────────────────────────────────────────── */}
      <SettingsSection title="Urgency &amp; Priority">
        <div style={row2}>
          <NumField
            label="Red border score"
            hint="Score ≥ this → red card border."
            value={settings.urgencyRedScore ?? 60}
            min={1} max={200}
            onChange={(e) => update({ urgencyRedScore: Number(e.target.value) || 60 })}
          />
          <NumField
            label="Amber border score"
            hint="Score ≥ this → amber card border."
            value={settings.urgencyAmberScore ?? 30}
            min={1} max={200}
            onChange={(e) => update({ urgencyAmberScore: Number(e.target.value) || 30 })}
          />
        </div>
        <div style={row2}>
          <NumField
            label="Stale contact — mild (days)"
            hint="Adds +10 urgency after this many days without contact."
            value={settings.staleDays1 ?? 21}
            min={1} max={365}
            onChange={(e) => update({ staleDays1: Number(e.target.value) || 21 })}
          />
          <NumField
            label="Stale contact — strong (days)"
            hint="Adds +20 urgency after this many days without contact."
            value={settings.staleDays2 ?? 60}
            min={1} max={365}
            onChange={(e) => update({ staleDays2: Number(e.target.value) || 60 })}
          />
        </div>
        <NumField
          label="Next step overdue grace period (days)"
          hint="How many days past the due date before the higher urgency score (+45 vs +35) kicks in."
          value={settings.nextStepOverdueDays ?? 3}
          min={0} max={30}
          onChange={(e) => update({ nextStepOverdueDays: Number(e.target.value) ?? 3 })}
        />
      </SettingsSection>

      {/* ── Trial settings ───────────────────────────────────────────────── */}
      <SettingsSection title="Trial Settings">
        <div style={row2}>
          <NumField
            label="Default trial length (days)"
            hint="Pre-filled end date when creating a new trial."
            value={settings.trialDefaultDays ?? 30}
            min={1} max={365}
            onChange={(e) => update({ trialDefaultDays: Number(e.target.value) || 30 })}
          />
          <div />
        </div>
        <div style={row2}>
          <NumField
            label="Renewal warning — red (days)"
            hint="Renewal date within this many days shows red in Customers."
            value={settings.renewalRedDays ?? 30}
            min={1} max={365}
            onChange={(e) => update({ renewalRedDays: Number(e.target.value) || 30 })}
          />
          <NumField
            label="Renewal warning — amber (days)"
            hint="Renewal date within this many days shows amber."
            value={settings.renewalAmberDays ?? 60}
            min={1} max={365}
            onChange={(e) => update({ renewalAmberDays: Number(e.target.value) || 60 })}
          />
        </div>
      </SettingsSection>

      {/* ── BD / Cold Outreach ──────────────────────────────────────────── */}
      <SettingsSection title="BD &amp; Cold Outreach">
        <div style={row2}>
          <NumField
            label="BD high-intent window (days)"
            hint="How far back to look for email opens/clicks when surfacing BD suggestions."
            value={settings.bdHighIntentDays ?? 14}
            min={1} max={90}
            onChange={(e) => update({ bdHighIntentDays: Math.max(1, Number(e.target.value) || 14) })}
          />
          <NumField
            label="Batch min recipients"
            hint="Minimum recipients for a cold outreach send to appear as a batch in Today."
            value={settings.bdBatchMin ?? 3}
            min={1} max={50}
            onChange={(e) => update({ bdBatchMin: Math.max(1, Number(e.target.value) || 3) })}
          />
        </div>
      </SettingsSection>

      {/* ── Pipeline weights ─────────────────────────────────────────────── */}
      <SettingsSection title="Pipeline Probability Weights">
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 10 }}>
          Used to compute weighted ARR in Pipeline and Contacts. Set to your actual historical close rates (0–1).
        </div>
        <div style={row2}>
          <NumField label="Trial" value={settings.stageWeightTrial ?? 0.6} min={0} max={1} step={0.05}
            onChange={(e) => update({ stageWeightTrial: Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)) })} />
          <NumField label="Demo" value={settings.stageWeightDemo ?? 0.3} min={0} max={1} step={0.05}
            onChange={(e) => update({ stageWeightDemo: Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)) })} />
          <NumField label="Warm" value={settings.stageWeightWarm ?? 0.1} min={0} max={1} step={0.01}
            onChange={(e) => update({ stageWeightWarm: Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)) })} />
          <NumField label="Cold" value={settings.stageWeightCold ?? 0.05} min={0} max={1} step={0.01}
            onChange={(e) => update({ stageWeightCold: Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)) })} />
          <NumField label="Cold (untouched)" value={settings.stageWeightUntouched ?? 0.02} min={0} max={1} step={0.01}
            onChange={(e) => update({ stageWeightUntouched: Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)) })} />
        </div>
      </SettingsSection>

      {/* ── Health scoring ───────────────────────────────────────────────── */}
      <SettingsSection title="Trial Health Scoring">
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 10 }}>
          Adjust what counts as &quot;active&quot; login recency and feature usage for your product's typical patterns.
        </div>
        <div style={row2}>
          <NumField
            label="Login: healthy (≤ days)"
            hint="Logged in within this many days = full recency score."
            value={settings.healthLoginGreenDays ?? 3}
            min={1} max={60}
            onChange={(e) => update({ healthLoginGreenDays: Number(e.target.value) || 3 })}
          />
          <NumField
            label="Login: at-risk (≤ days)"
            hint="Logged in within this many days = partial recency score."
            value={settings.healthLoginAmberDays ?? 7}
            min={1} max={90}
            onChange={(e) => update({ healthLoginAmberDays: Number(e.target.value) || 7 })}
          />
          <NumField
            label="Features: deep usage (≥ count)"
            hint="Using at least this many distinct features = full depth score."
            value={settings.healthFeatureGreenCount ?? 3}
            min={1} max={20}
            onChange={(e) => update({ healthFeatureGreenCount: Number(e.target.value) || 3 })}
          />
          <NumField
            label="Features: some usage (≥ count)"
            hint="Using at least this many = partial depth score."
            value={settings.healthFeatureAmberCount ?? 2}
            min={1} max={20}
            onChange={(e) => update({ healthFeatureAmberCount: Number(e.target.value) || 2 })}
          />
        </div>
      </SettingsSection>

      {/* ── Sync config ──────────────────────────────────────────────────── */}
      <SettingsSection title="Sync Settings">
        <label style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)', marginBottom: 10 }}>
          Demo calendar keywords (comma-separated)
          <input
            type="text"
            value={settings.calendarKeywords ?? 'demo,implied'}
            onChange={(e) => update({ calendarKeywords: e.target.value })}
            style={inp}
            placeholder="demo,walkthrough,intro call"
          />
          <span style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginTop: 3 }}>
            Calendar events whose title contains any of these words are synced as demos.
          </span>
        </label>
        <label style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)', marginBottom: 0 }}>
          Internal email domains
          <div style={{ marginTop: 5 }}>
            <DomainListEditor
              value={settings.internalDomains ?? 'implied.com,lumosity.ai'}
              onChange={(e) => update({ internalDomains: e.target.value })}
              placeholder="yourcompany.com"
            />
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginTop: 3 }}>
            Emails from these domains are treated as internal staff and ignored everywhere unmatched attendees/senders are shown ("not in contacts" badges, Match/Add prompts) — Demos, Calendar sync, Amplitude, and elsewhere.
          </span>
        </label>

        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)' }}>
            <input
              type="checkbox"
              checked={settings.autoSyncEnabled !== false}
              onChange={(e) => update({ autoSyncEnabled: e.target.checked })}
            />
            Automatic Gmail + Calendar sync (every 3 hours)
          </label>
          <label style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)', marginTop: 10 }}>
            Mailboxes to sync automatically (comma-separated)
            <input
              type="text"
              value={settings.autoSyncUsers ?? 'ying@implied.com'}
              onChange={(e) => update({ autoSyncUsers: e.target.value })}
              style={inp}
              placeholder="ying@implied.com"
            />
            <span style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginTop: 3 }}>
              The scheduled sync can't see the 👥 picker (that's per-browser), so it uses this list instead. Only mailboxes that have connected Gmail are synced; anyone else here is skipped. Manual Sync buttons still follow the 👥 picker.
            </span>
          </label>
        </div>
      </SettingsSection>

      {/* ── BD cadence (First Send / Follow-up schedule) ────────────────────── */}
      <SettingsSection title="Cadence Settings">
        <CadenceRulesEditor
          rulesJson={settings.cadenceStageRules ?? ''}
          legacyOffsets={settings.cadenceStageOffsets ?? '0,2,5,12'}
          legacyPatterns={settings.cadenceTrackedPatterns ?? ''}
          onChange={(json) => update({ cadenceStageRules: json })}
        />
      </SettingsSection>

      {/* ── Sales brief email ────────────────────────────────────────────── */}
      <SettingsSection title="Sales Brief Email">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)', marginBottom: 10 }}>
          <input type="checkbox" checked={settings.briefEnabled !== false} onChange={(e) => update({ briefEnabled: e.target.checked })} />
          Send the daily brief
        </label>
        <label style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)', marginBottom: 10 }}>
          Recipients (comma-separated)
          <input
            type="text"
            value={settings.briefRecipients ?? ''}
            onChange={(e) => update({ briefRecipients: e.target.value })}
            style={inp}
            placeholder="you@company.com, teammate@company.com"
          />
        </label>
        <label style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)', marginBottom: 10 }}>
          Tone
          <select
            value={settings.briefTone ?? 'concise'}
            onChange={(e) => update({ briefTone: e.target.value })}
            style={inp}
          >
            <option value="concise">Concise</option>
            <option value="warm">Warm</option>
            <option value="direct">Direct</option>
          </select>
        </label>
        <label style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)', marginBottom: 10 }}>
          Primary focus
          <input
            type="text"
            value={settings.briefFocus ?? 'pipeline momentum'}
            onChange={(e) => update({ briefFocus: e.target.value })}
            style={inp}
            placeholder="e.g. pipeline momentum"
          />
        </label>
        <div style={row2}>
          <label style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)', marginBottom: 10 }}>
            Send time
            <input type="time" value={settings.briefSendTime ?? '08:30'} onChange={(e) => update({ briefSendTime: e.target.value })} style={inp} />
          </label>
          <div />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)', marginBottom: 12 }}>
          <input type="checkbox" checked={!!settings.briefIncludeStats} onChange={(e) => update({ briefIncludeStats: e.target.checked })} />
          Include quick stats in the email
        </label>

        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '4px 0 8px' }}>
          Sections
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)', marginBottom: 6 }}>
          <input type="checkbox" checked={settings.briefSectionMustAct !== false} onChange={(e) => update({ briefSectionMustAct: e.target.checked })} />
          Must Act Today / Push This Week
        </label>
        <label style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>
          Must Act company cap
          <input type="number" min="0" value={settings.briefUrgentGroupCap ?? 6}
            onChange={(e) => update({ briefUrgentGroupCap: Number(e.target.value) || 0 })} style={inp} />
        </label>
        <label style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
          Push This Week company cap
          <input type="number" min="0" value={settings.briefWatchGroupCap ?? 8}
            onChange={(e) => update({ briefWatchGroupCap: Number(e.target.value) || 0 })} style={inp} />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)', marginBottom: 6 }}>
          <input type="checkbox" checked={settings.briefSectionNeedsReply !== false} onChange={(e) => update({ briefSectionNeedsReply: e.target.checked })} />
          Needs Reply
        </label>
        <div style={row2}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
            Max unanswered emails shown
            <input type="number" min="0" value={settings.briefNeedsReplyLimit ?? 25}
              onChange={(e) => update({ briefNeedsReplyLimit: Number(e.target.value) || 0 })} style={inp} />
          </label>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
            Lookback window (hours)
            <input type="number" min="1" value={settings.briefNeedsReplyHours ?? 72}
              onChange={(e) => update({ briefNeedsReplyHours: Number(e.target.value) || 0 })} style={inp} />
          </label>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)', marginBottom: 6 }}>
          <input type="checkbox" checked={settings.briefSectionUsageDrop !== false} onChange={(e) => update({ briefSectionUsageDrop: e.target.checked })} />
          Usage Drop Alert
        </label>
        <label style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginBottom: 0 }}>
          Max total shown
          <input type="number" min="0" value={settings.briefUsageDropMax ?? 8}
            onChange={(e) => update({ briefUsageDropMax: Number(e.target.value) || 0 })} style={inp} />
        </label>
      </SettingsSection>

      {/* ── GTM / Outreach ───────────────────────────────────────────────── */}
      <GtmSettingsSection getToken={getToken} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
          {saveError ? <span style={{ color: '#ef4444' }}>{saveError}</span> : 'Changes apply instantly. Click Save to persist across devices.'}
        </div>
        <button className="btn btn--primary btn--sm" onClick={() => onSave?.(settings)} disabled={saving}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}
